import { ref, type Ref } from 'vue';
import { safeGetItem, safeSetItem } from 'Utils/safeLocalStorage';
import { useCurrentSite } from 'Composables/useCurrentSite';

/**
 * Every role value the counselor slider/grid can resolve to, so the rest of
 * the app can reference `ROLE.LAWYER` instead of hardcoding `'lawyer'`.
 *
 * `GENERIC` is the server's fallback role on pages with no page type, and
 * `ALL_TYPES` is what the counselor slider/grid Gutenberg blocks emit for
 * their "All types" default — both mean "no specific page type" below.
 */
export const ROLE = {
  LAWYER: 'lawyer',
  THERAPY: 'therapy',
  FUNERAL: 'funeral',
  GENERIC: 'user',
  ALL_TYPES: '',
} as const;

export type RoleValue = (typeof ROLE)[keyof typeof ROLE];

/**
 * Roles that map to a real page type (ärendetyp). Only these are persisted so a
 * visitor keeps seeing the right advisor type when they later land on a generic
 * page (startsida, "om oss", etc.) where the server falls back to `user`.
 *
 * `funeral` is a page-type marker only: funeral pages can't emit `user` since
 * that is indistinguishable from a generic page, so they emit `funeral` and it
 * is translated back to the `user` API role in resolveApiRole().
 */
export const PERSISTABLE_ROLES = [
  ROLE.LAWYER,
  ROLE.THERAPY,
  ROLE.FUNERAL,
] as const;

type PersistableRole = (typeof PERSISTABLE_ROLES)[number];

function isPersistableRole(
  role: string | null | undefined
): role is PersistableRole {
  return (PERSISTABLE_ROLES as readonly string[]).includes(role ?? '');
}

// The counselor slider Gutenberg block emits '' for its "All types" default.
const GENERIC_ROLES: readonly string[] = [ROLE.GENERIC, ROLE.ALL_TYPES];

function toApiRole(role: string): string {
  return role === ROLE.FUNERAL ? ROLE.GENERIC : role;
}

/**
 * Resolve the role to send to the advisors API. On a page with a known page
 * type the page role always wins (and is persisted separately). On a generic
 * page (`user`, or `''` from an "All types" slider) we fall back to a
 * previously stored role, keeping today's generic behavior when nothing is
 * stored. Stored values outlive deployments, so anything outside
 * PERSISTABLE_ROLES is ignored rather than sent to the API.
 */
export function resolveApiRole(
  pageRole: string,
  storedRole: string | null
): string {
  if (!GENERIC_ROLES.includes(pageRole)) {
    return toApiRole(pageRole);
  }

  if (isPersistableRole(storedRole)) {
    return toApiRole(storedRole);
  }

  return pageRole;
}

export function useRoleTermLocalStorage() {
  const { currentSiteUpperCase } = useCurrentSite();

  function getRoleTermFromLocalStorage(): string | null {
    return safeGetItem(`roleTerm-${currentSiteUpperCase.value}`);
  }

  const roleTerm: Ref<string | null> = ref(getRoleTermFromLocalStorage());

  function saveRoleTermToLocalStorage(role: string): void {
    // Never let a generic page overwrite a previously stored page-type role.
    if (!isPersistableRole(role)) {
      return;
    }

    safeSetItem(`roleTerm-${currentSiteUpperCase.value}`, role);
    roleTerm.value = role;
  }

  return {
    roleTerm,
    saveRoleTermToLocalStorage,
    getRoleTermFromLocalStorage,
  };
}
